/**
 * System → Admins (system.admin.list).
 *
 * CRUD over admin_users. Shows assigned roles as pills.
 * Creating / editing admins opens a modal form; backend hashes password.
 */
import { useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface AdminRow {
  id: number;
  username: string;
  status: string;
  create_time: string;
  roles: { id: number; name: string; preset: string | null }[];
}

interface RoleRow {
  id: number;
  name: string;
  preset: string | null;
}

export function AdminsPage() {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('system.admin.create');

  const { data: rolesResp } = useQuery({
    queryKey: ['admin', 'roles', 'all'],
    queryFn: () => api.get<{ total: number; rows: RoleRow[] }>('/api/admin/roles', { page: 1, limit: 100 }),
  });

  async function handleCreate(values: { username: string; password: string; role_ids: number[] }) {
    try {
      await api.post('/api/admin/admins', values);
      message.success('管理员已创建');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'admins'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用户名', dataIndex: 'username' },
    {
      title: '角色',
      key: 'roles',
      render: (_: unknown, row: AdminRow) => (
        <Space size={4} wrap>
          {(row.roles ?? []).map((r) => (
            <Tag key={r.id} color={r.preset === 'super' ? 'red' : r.preset ? 'purple' : 'blue'}>
              {r.name}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) =>
        v === 'active' ? <Tag color="green">正常</Tag> : <Tag>{v}</Tag>,
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setOpen(true)}>
            新建管理员
          </Button>
        )}
      </Space>

      <DataTable<AdminRow>
        queryKey={['admin', 'admins']}
        endpoint="/api/admin/admins"
        columns={columns}
        rowKey="id"
      />

      <Modal
        open={open}
        title="新建管理员"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, min: 8 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="role_ids" label="角色" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              options={(rolesResp?.rows ?? []).map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
